import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useRef, useState } from 'react';
import { Link } from 'react-router';

import { toConsentDateLabel, useSession } from '@/entities/session';
import { useTRPC } from '@/shared/api';

/** 광고성 정보 수신 동의의 현재 상태와 변경 이력을 계정에서 관리한다. */
export function MarketingConsentSection(): ReactElement | null {
  const member = useSession((state) => state.member);
  const marketingEmailOptIn = useSession((state) => state.marketingEmailOptIn);
  const marketingConsentAt = useSession((state) => state.marketingConsentAt);
  const setMarketingOptIn = useSession((state) => state.setMarketingOptIn);

  const [notice, setNotice] = useState('');
  const isChanging = useRef(false);

  const trpc = useTRPC();
  const { mutateAsync, isPending } = useMutation(
    trpc.auth.changeMarketingConsent.mutationOptions(),
  );

  if (!member) return null;

  /**
   * 서버에 남기고 나서 화면을 바꾼다.
   *
   * **미리 바꿔 두지 않는다.** 저장이 실패했는데 켜진 채로 두면 사용자는 동의한 줄 알고
   * 나가지만 서버에는 그 기록이 없다 — 광고를 못 받거나, 반대로 껐다고 믿는 사람에게
   * 계속 나간다. 어느 쪽이든 우리가 지는 자리다.
   *
   * 동의일도 **서버가 돌려준 것**만 쓴다. 화면이 자기 시계로 만들면 증빙과 어긋난다.
   */
  const toggle = async (next: boolean) => {
    // `disabled` 는 리렌더가 커밋된 뒤에야 듣는다. 그 틈에 두 번째 클릭이 들어오면
    //   true·false 두 요청이 같이 나가고, **늦게 도착한 쪽**이 최종 상태가 된다 —
    //   마지막으로 누른 것과 다른 값이 저장될 수 있다.
    if (isChanging.current) return;
    isChanging.current = true;

    // 응답을 반영할 때 "아직 같은 사람인가"를 확인하기 위한 기준점.
    const requestedBy = member.email;

    try {
      const saved = await mutateAsync({ granted: next });

      // 이 응답은 컴포넌트가 사라져도 살아남는다. 공용 브라우저에서 앞사람이 누른 뒤
      //   로그아웃하고 다음 사람이 들어오면, 늦게 온 응답이 **그 사람 설정을 덮는다.**
      if (useSession.getState().member?.email !== requestedBy) return;

      setMarketingOptIn(
        saved.marketingEmail.granted,
        toConsentDateLabel(saved.marketingEmail.occurredAt) ?? '',
      );
      setNotice(next ? '수신 동의 처리되었습니다.' : '수신 거부 처리되었습니다.');
    } catch {
      // 무엇이 틀렸는지는 말하지 않는다 — 사용자가 할 수 있는 일은 다시 누르는 것뿐이다.
      setNotice('저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      isChanging.current = false;
    }
  };

  return (
    <section aria-labelledby="account-marketing-heading" className="flex flex-col gap-3">
      <h2
        id="account-marketing-heading"
        className="text-[1.0625rem] font-semibold tracking-[-0.03em]"
      >
        광고성 정보 수신
      </h2>

      <div className="flex flex-col gap-3 rounded-lg border border-border p-5">
        <label htmlFor="account-marketing" className="flex items-start gap-2 text-[14px]">
          <input
            id="account-marketing"
            type="checkbox"
            checked={marketingEmailOptIn}
            disabled={isPending}
            onChange={(event) => void toggle(event.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-primary"
          />
          <span>새 자료·클래스 소식을 이메일로 받겠습니다</span>
        </label>

        {marketingEmailOptIn && marketingConsentAt ? (
          <p className="text-[12.5px] text-muted-foreground">
            동의일{' '}
            <span data-numeric="" className="font-mono">
              {marketingConsentAt}
            </span>
          </p>
        ) : null}

        <p className="text-[12.5px] text-muted-foreground">
          설정을 바꾸면 그 시점부터 적용됩니다. 자세한 내용은{' '}
          <Link to="/marketing-consent" className="underline underline-offset-2">
            광고성 정보 수신 동의
          </Link>
          를 확인해 주세요.
        </p>
      </div>

      {notice ? (
        <p
          role="status"
          className="rounded-md border border-border border-l-[3px] px-3 py-2 text-[13px]"
        >
          {notice}
        </p>
      ) : null}
    </section>
  );
}
